/* @flow */

import * as React from 'react';

import {ErrorMessage} from 'shells/form';

import {AlertBox} from '../alert-box';
import {Button, SaveButton} from '../button';
import {portal} from '../portal';
import {Header, Subheader} from '../typography';

import './confirmation-dialog.module.css';

type Props = {|
    title?: string,
    message: string | React.Element<*>,
    confirmText?: string | React.Node,
    cancelText?: string,
    onCancel: (e?: SyntheticEvent<*>) => void,
    onConfirm?: (e: SyntheticEvent<*>) => void,
    isConfirmDestructive?: boolean,
    isSaving?: boolean,
    customSaveText?: {
        initial: string,
        saving: string,
        complete: string,
    },
    errorMessage?: string,
    autoFocusCancelButton?: boolean,
|};

function ConfirmationDialogComponent(props: Props) {
    // Destructure and assigning a default works better (in general, but also for Flow) than using defaultProps
    const {
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        isConfirmDestructive = true,
        autoFocusCancelButton = true,
    } = props;

    const cancelButtonRef = React.useRef(undefined);

    React.useLayoutEffect(() => {
        if (cancelButtonRef && cancelButtonRef.current) {
            cancelButtonRef.current.focus();
        }
    }, []);

    const customSaveText = props.customSaveText ? props.customSaveText : {};

    let confirmButton;
    if (props.onConfirm && typeof confirmText === 'string') {
        confirmButton = (
            <SaveButton
                type='button'
                variant={isConfirmDestructive ? 'danger' : 'primary'}
                onClick={props.onConfirm}
                isSaving={Boolean(props.isSaving)}
                buttonText={customSaveText.initial || confirmText}
                savingText={customSaveText.saving}
                successText={customSaveText.complete}
                disabled={Boolean(props.errorMessage)}
            />
        );
    } else if (confirmText && typeof confirmText !== 'string') {
        // Confirm text is a custom component
        confirmButton = confirmText;
    }

    return (
        <AlertBox onClose={props.onCancel}>
            <div styleName='confirm-box'>
                {props.errorMessage && (
                    <div className='pb-8'>
                        <ErrorMessage>{props.errorMessage}</ErrorMessage>
                    </div>
                )}
                <div className='mb-16'>
                    {props.title && <Header>{props.title}</Header>}
                    <Subheader>{props.message}</Subheader>
                </div>
                <div styleName='confirm-buttons'>
                    {props.onCancel && (
                        <Button
                            variant={isConfirmDestructive ? 'text-primary' : 'text-secondary'}
                            onClick={props.onCancel}
                            getButtonRef={autoFocusCancelButton ? cancelButtonRef : undefined}
                        >
                            {cancelText}
                        </Button>
                    )}
                    {confirmButton}
                </div>
            </div>
        </AlertBox>
    );
}

export const ConfirmationDialog = portal(ConfirmationDialogComponent);
